/**
 * CRound-aware money formatting.
 *
 * CRound is asymmetric arithmetic rounding ported from the backend
 * A5Core.Domain/ValueObjects/CRound.cs. The algorithm:
 *   factor  = 10^decimals
 *   floored = Math.floor(value * factor)
 *   if (floored + 0.5 > value * factor)  return floored / factor
 *   else                                  return (floored + 1) / factor
 *
 * Key difference from Math.round: 0.5 ALWAYS rounds UP (away from zero for
 * positives, toward zero for negatives). This matches the legacy VB.NET
 * CRound used by every financial calculation in the system.
 *
 * Display format: Lithuanian/European — `1 234,56 €`
 */

function cround(value: number, decimals: number = 2): number {
  const factor = Math.pow(10, decimals);
  const shifted = value * factor;
  const floored = Math.floor(shifted);
  if (floored + 0.5 > shifted) {
    return floored / factor;
  } else {
    return (floored + 1) / factor;
  }
}

/**
 * Format a number as Lithuanian/European money string.
 * Example: 1234.56 → "1 234,56 €"
 * Negative: -1234.56 → "-1 234,56 €"
 */
export function formatMoney(value: number | undefined | null, showCurrency = true): string {
  if (value == null) return showCurrency ? '0,00 €' : '0,00';

  const rounded = cround(value);
  const isNegative = rounded < 0;
  const abs = Math.abs(rounded);

  // Split into integer and decimal parts
  const fixed = abs.toFixed(2);
  const parts = fixed.split('.');
  const intPart = parts[0] ?? '0';
  const decPart = parts[1] ?? '00';

  // Add space as thousands separator
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');

  const formatted = `${withSpaces},${decPart}`;
  const sign = isNegative ? '-' : '';
  const currency = showCurrency ? ' €' : '';

  return `${sign}${formatted}${currency}`;
}

/**
 * Format a number as a percentage with 2 decimal places.
 * Example: 0.21 → "21,00%"
 */
export function formatPercent(value: number): string {
  const pct = cround(value * 100);
  const fixed = pct.toFixed(2);
  const parts = fixed.split('.');
  const intPart = parts[0] ?? '0';
  const decPart = parts[1] ?? '00';
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
  return `${withSpaces},${decPart}%`;
}

/**
 * Parse a Lithuanian-format money string back to a number.
 * "1 234,56" → 1234.56
 */
export function parseMoney(str: string): number {
  const cleaned = str.replace(/\s/g, '').replace(/\u00A0/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : cround(num);
}

export { cround };
